import { useEffect, useRef, useState } from 'react'
import * as ReactDOM from 'react-dom'

import { Box, Hint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0Callout } from '@factorialco/f0-react/dist/experimental'
import { Settings } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import type { LeaveApprovalRecommendationFragment } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { RECOMMENDATION_ACTION, RecommendationAction } from './RecommendationAction'
import sdmVideo from './assets/AutomationsAndSmartDecision.mp4'
import {
  RecommendationReason,
  getRecommendationDescriptionRich,
} from './getRecommendationDescriptionRich'

interface Props {
  employeeName: string
  employeeId: string
  recommendation: LeaveApprovalRecommendationFragment | null | undefined
  workable?: boolean
}

const LeaveApprovalRecommendation = ({
  employeeName,
  employeeId,
  recommendation,
  workable,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const storageKey = `${LOCAL_STORAGE_KEYS.leaveApprovalRecommendationModal}:${employeeId}`
  const [hasSeenModal, setHasSeenModal] = useLocalStorage<boolean>(storageKey, false)
  const hasTrackedButtonView = useRef(false)

  // Track when the "Setup personalized rule" button is viewed (first step of the funnel)
  useEffect(() => {
    if (!hasSeenModal && !hasTrackedButtonView.current) {
      hasTrackedButtonView.current = true
      track(trackingEvents.timeoff.leaveApprovalRecommendation.personalizedRuleModal.buttonViewed, {
        employee_name: employeeName,
        employee_id: employeeId,
        resource_type: 'timeoff',
      })
    }
  }, [hasSeenModal, employeeName, employeeId])

  // Use ImageStep with video src and replace rendered img with video element
  // VideoStep doesn't properly render children (description and button), so we use ImageStep
  // and replace the img element with a video that has autoplay enabled
  useEffect(() => {
    if (isModalOpen) {
      const timer = setTimeout(() => {
        // Try multiple selectors to find the image rendered by ImageStep
        const selectors = [
          '[data-hint-image-step] img',
          '.hint-image-step img',
          'img[src*="AutomationsAndSmartDecision"]',
          'img[src*="sdm"]',
        ]
        let img: HTMLImageElement | null = null
        for (const selector of selectors) {
          img = document.querySelector(selector)
          if (img) break
        }

        if (img && img.src.includes('mp4')) {
          const video = document.createElement('video')
          video.src = sdmVideo
          video.autoplay = true
          video.muted = true
          video.playsInline = true
          video.loop = true
          video.style.width = '100%'
          video.style.height = 'auto'
          video.style.display = 'block'
          img.parentNode?.replaceChild(video, img)
        }
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [isModalOpen])

  const handleModalClose = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    track(
      trackingEvents.timeoff.leaveApprovalRecommendation.personalizedRuleModal.imInterestedClicked,
      {
        employee_name: employeeName,
        employee_id: employeeId,
        resource_type: 'timeoff',
      }
    )
    setHasSeenModal(true)
    setIsModalOpen(false)
    app.setFlash({
      type: 'correct',
      message: i18n.t('time_off.approval_recommendation.modal_success_message'),
    })
  }

  const handleModalOpen = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    track(trackingEvents.timeoff.leaveApprovalRecommendation.personalizedRuleModal.modalOpened, {
      employee_name: employeeName,
      employee_id: employeeId,
      resource_type: 'timeoff',
    })
    setIsModalOpen(true)
  }

  const onFinish = () => {
    if (!hasSeenModal) {
      setIsModalOpen(true)
    }
  }

  const getRecommendationText = (action: RecommendationAction) => {
    switch (action) {
      case RECOMMENDATION_ACTION.APPROVE:
        return i18n.t('time_off.approval_recommendation.approval_suggested')
      case RECOMMENDATION_ACTION.REVIEW:
        return i18n.t('time_off.approval_recommendation.review_needed')
      case RECOMMENDATION_ACTION.NO_RECOMMENDATION:
        return i18n.t('time_off.approval_recommendation.not_enough_information')
      default:
        return ''
    }
  }
  const getRecommendationType = (action: RecommendationAction) => {
    switch (action) {
      case RECOMMENDATION_ACTION.APPROVE:
        return 'positive'
      case RECOMMENDATION_ACTION.REVIEW:
        return 'warning'
      case RECOMMENDATION_ACTION.NO_RECOMMENDATION:
        return 'info'
      default:
        return 'info'
    }
  }

  const recommendedAction = recommendation?.action
  const recommendationAction: RecommendationAction | '' =
    recommendedAction === RECOMMENDATION_ACTION.APPROVE ||
    recommendedAction === RECOMMENDATION_ACTION.REVIEW ||
    recommendedAction === RECOMMENDATION_ACTION.NO_RECOMMENDATION
      ? recommendedAction
      : ''
  const recommendedReason = recommendation?.reason

  const recommendationReason: RecommendationReason | '' =
    recommendedReason && typeof recommendedReason === 'object' ? recommendedReason : ''

  if (!recommendationAction) return null

  const recommendationText = getRecommendationText(recommendationAction)
  const recommendationType = getRecommendationType(recommendationAction)
  const recommendationDescription = getRecommendationDescriptionRich(
    recommendationAction,
    recommendationReason,
    employeeName,
    workable
  )
  const buildModalDescription = () => {
    return (
      <Box textAlign="center">{i18n.t('time_off.approval_recommendation.modal_description')}</Box>
    )
  }

  return (
    <>
      <F0Callout
        title={recommendationText}
        variant={recommendationType}
        actions={
          !hasSeenModal
            ? [
                {
                  label: `${i18n.t('time_off.approval_recommendation.callout_button')}`,
                  onClick: handleModalOpen,
                  icon: Settings,
                },
              ]
            : []
        }
      >
        {recommendationDescription}
      </F0Callout>

      {isModalOpen &&
        // Use portal to render modal directly in body, ensuring it appears above sidebar and other content
        // regardless of parent container z-index
        ReactDOM.createPortal(
          <Hint.NewModal onClose={() => setIsModalOpen(false)} onFinish={onFinish} maxWidth="s12">
            {/* Use ImageStep instead of VideoStep because VideoStep doesn't render children properly.
                The description prop renders above the video, and children (button) render below it. */}
            <Hint.NewModal.ImageStep
              src={sdmVideo}
              title={''}
              description={buildModalDescription()}
            >
              <Box textAlign="center" alignItems="center" justifyContent="center">
                <F0Button
                  label={i18n.t('time_off.approval_recommendation.modal_button')}
                  onClick={handleModalClose}
                  size="md"
                  variant="default"
                  weight="medium"
                  type="button"
                />
              </Box>
            </Hint.NewModal.ImageStep>
          </Hint.NewModal>,
          document.body
        )}
    </>
  )
}

export default LeaveApprovalRecommendation
